import { AnalyticsConfig, ReportDetailed, TimeRangeConfig, VariableSlug } from '@/api/generated/model';
import { EChartKind } from '@/api/requests/types/charts.type';
import { sanitizeAnalyticsRollingWindow } from '@/domains/chart/utils/rolling-window';
import { getTimezoneName } from '@/utils/date.util';
import { getDateTRangeFromKindWithoutCustom } from '@/utils/get-date-range-from-kind';
import { isFilterDefined } from '@/models/common/filters';
import { splitFilters } from '@/utils/filter-utils';

export const mapAnalyticsConfiguration = (chartConf: AnalyticsConfig, report?: ReportDetailed): AnalyticsConfig => {
  const isMetricChart = chartConf.chart_type === EChartKind.METRIC;

  const { kind, start, end } = getDateTRangeFromKindWithoutCustom('30_days');

  // TODO: handle empty object case
  let timeRange: TimeRangeConfig = {
    kind: kind,
    start: start,
    end: end,
    timezone: getTimezoneName(),
  };

  if (Object.keys(chartConf.time_range).length > 0) {
    timeRange = {
      kind: chartConf.time_range.kind,
      start: chartConf.time_range.start,
      end: chartConf.time_range.end,
      timezone: getTimezoneName(),
    };
  }

  // Combine all filters from chart config and report (both regular and special filters)
  const reportRegularFilters = report?.configuration?.filters ?? [];
  const reportSpecialFilters = report?.configuration?.special_filters ?? [];
  const allFilters = chartConf.filters
    .concat(reportRegularFilters)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .concat(reportSpecialFilters as any) // Special filters from report
    .filter(isFilterDefined);

  const { regularFilters, specialFilters } = splitFilters(allFilters);

  return sanitizeAnalyticsRollingWindow({
    ...chartConf,
    filters: regularFilters,
    special_filters: specialFilters as AnalyticsConfig['special_filters'],
    time_range: {
      ...timeRange,
    },
    variables: remapVariables(resolveVariables(chartConf, isMetricChart)),
  });
};

const resolveVariables = (chartConf: AnalyticsConfig, isMetricChart: boolean): VariableSlug[] => {
  const standardVariables = chartConf.variables ?? [];
  const hasCustomVariables = (chartConf.custom_variables?.length ?? 0) > 0;

  if (isMetricChart) {
    if (standardVariables.length > 0) {
      return [standardVariables[0]];
    }
    if (hasCustomVariables) {
      return [];
    }
    return ['users'];
  }

  if (standardVariables.length > 0 || hasCustomVariables) {
    return standardVariables;
  }

  return ['users'];
};

const remapVariables = (variables: VariableSlug[]): VariableSlug[] => {
  return (
    variables?.map((variable) => {
      if (variable === 'prompts_to_value') return 'conversation_length';
      return variable;
    }) ?? []
  );
};
